import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

/** 跟随全站 i18n：'ja' | 'zh' */
type Lang = 'ja' | 'zh';
type LJ = { ja: string; zh: string };

const copy = {
  title: { ja: '情報セキュリティポリシー', zh: '信息安全方针' } as LJ,
  intro: {
    ja: '本ページでは、SMHC株式会社（以下「当社」）の情報セキュリティに関する基本方針を記載しています。',
    zh: '本页面说明 SMHC株式会社（以下简称“本公司”）的信息安全基本方针。',
  } as LJ,
  sections: {
    s1_title: { ja: '1. 基本方針', zh: '1. 基本方针' } as LJ,
    s1_body: {
      ja: '当社は、顧客からお預かりした情報資産を含むすべての情報資産を保護することを重要な責務と認識し、情報セキュリティの確保に努めます。法令やガイドラインを遵守し、安全で信頼されるサービスを提供いたします。',
      zh: '本公司深知保护包括客户委托的信息资产在内的一切信息资产是重要职责，持续致力于信息安全保障；同时遵守相关法律与指引，向用户提供安全且值得信赖的服务。',
    } as LJ,

    s2_title: { ja: '2. 適用範囲', zh: '2. 适用范围' } as LJ,
    s2_body: {
      ja: '本ポリシーは、当社の役員、社員、および業務に従事する協力会社のスタッフが取り扱うすべての情報資産に適用されます。',
      zh: '本方针适用于本公司的董事、员工及参与本公司业务的合作公司人员所处理的一切信息资产。',
    } as LJ,

    s3_title: { ja: '3. 情報資産の保護', zh: '3. 信息资产的保护' } as LJ,
    s3_items: {
      ja: [
        '個人情報・機密情報・顧客データを適切に分類し、アクセス権限を最小限に制御します。',
        'ネットワークやシステムに対し、不正アクセスや情報漏洩を防止するための技術的・物理的対策を講じます。',
      ],
      zh: [
        '对个人信息、机密信息及客户数据进行适当分级，并遵循最小权限原则进行访问控制。',
        '针对网络与系统采取技术与物理安全措施，防止未授权访问与信息泄露。',
      ],
    } as { ja: string[]; zh: string[] },

    s4_title: { ja: '4. 法令・規範の遵守', zh: '4. 遵守法律与规范' } as LJ,
    s4_body: {
      ja: '情報セキュリティに関する法令、規範、契約上の要求事項を遵守します。',
      zh: '本公司遵守与信息安全相关的法律、规范及合同要求。',
    } as LJ,

    s5_title: { ja: '5. 教育・啓発', zh: '5. 教育与宣导' } as LJ,
    s5_body: {
      ja: '従業員に対して定期的に情報セキュリティ教育を実施し、セキュリティ意識の向上に努めます。',
      zh: '本公司定期开展信息安全教育，不断提升员工的安全意识。',
    } as LJ,

    s6_title: { ja: '6. インシデント対応', zh: '6. 事件应对' } as LJ,
    s6_body: {
      ja: '万が一、情報セキュリティ上の事故・障害が発生した場合には、速やかに原因を究明し、影響を最小限に抑えるとともに、再発防止策を講じます。',
      zh: '如发生信息安全事故或故障，将迅速查明原因、将影响控制在最小范围，并制定与落实防止再次发生的措施。',
    } as LJ,

    s7_title: { ja: '7. 継続的改善', zh: '7. 持续改进' } as LJ,
    s7_body: {
      ja: '情報セキュリティ対策について、定期的に点検・見直しを行い、継続的な改善に努めます。',
      zh: '本公司将定期检查与审视信息安全措施，并持续推进改进。',
    } as LJ,

    s8_title: { ja: '8. お問い合わせ窓口', zh: '8. 联系方式' } as LJ,
    s8_lead: {
      ja: '本ポリシーに関するお問い合わせは、以下の窓口までお願いいたします。',
      zh: '关于本方针的咨询，请联系如下窗口：',
    } as LJ,

    s9_title: { ja: '9. 制定日', zh: '9. 制定日期' } as LJ,
    s9_body: { ja: '制定：2016年5月10日', zh: '制定：2016年5月10日' } as LJ,
  },
  contact: {
    company: { ja: 'SMHC株式会社', zh: 'SMHC株式会社' } as LJ,
    dept: { ja: '管理部　要害理恵', zh: '管理部 要害理恵' } as LJ,
    email_label: { ja: 'メール', zh: '邮箱' } as LJ,
    email: { ja: 'kanri@smhc.co.jp', zh: 'kanri@smhc.co.jp' } as LJ,
  },
};

const L = (x: LJ, lang: Lang) => x[lang];

const SecurityPolicyPage: React.FC = () => {
  const { i18n } = useTranslation();
  const lang: Lang = useMemo(() => (i18n.language?.toLowerCase().startsWith('zh') ? 'zh' : 'ja'), [i18n.language]);

  return (
    <div className="pt-0 sm:pt-14 pb-10 sm:pb-20 bg-gray-50 font-sans">
      <Helmet>
        <title>{L(copy.title, lang)} - Melodis Clinic</title>
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-4xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">
          {/* 标题（单语） */}
          <h1 className="text-xl md:text-2xl font-semibold text-slate-800 mb-6 text-center">
            {L(copy.title, lang)}
          </h1>

          {/* Intro（单语） */}
          <p className="text-slate-700 leading-relaxed mb-8 text-base md:text-lg">
            {L(copy.intro, lang)}
          </p>

          {/* 1 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.sections.s1_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.sections.s1_body, lang)}</p>
          </section>

          {/* 2 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.sections.s2_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.sections.s2_body, lang)}</p>
          </section>

          {/* 3 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.sections.s3_title, lang)}</h2>
            <ul className="list-disc pl-6 text-slate-700 space-y-2">
              {copy.sections.s3_items[lang].map((li, idx) => (
                <li key={idx}>{li}</li>
              ))}
            </ul>
          </section>

          {/* 4 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.sections.s4_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.sections.s4_body, lang)}</p>
          </section>

          {/* 5 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.sections.s5_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.sections.s5_body, lang)}</p>
          </section>

          {/* 6 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.sections.s6_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.sections.s6_body, lang)}</p>
          </section>

          {/* 7 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.sections.s7_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.sections.s7_body, lang)}</p>
          </section>

          {/* 8 联系方式 */}
          <section className="mb-10">
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.sections.s8_title, lang)}</h2>
            <div className="text-slate-700 leading-relaxed">
              <p>{L(copy.sections.s8_lead, lang)}</p>
              <div className="mt-3 rounded-xl border border-slate-200 p-4 bg-slate-50">
                <p>{L(copy.contact.company, lang)}</p>
                <p>{L(copy.contact.dept, lang)}</p>
                <p>
                  <a href={`mailto:${L(copy.contact.email, lang)}`} className="text-coral underline">
                    {L(copy.contact.email, lang)}
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* 9 制定日 */}
          <section>
            <h2 className="text-base font-semibold text-slate-800 mb-3">{L(copy.sections.s9_title, lang)}</h2>
            <p className="text-slate-700 leading-relaxed">{L(copy.sections.s9_body, lang)}</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SecurityPolicyPage;
